/**
 * f16.1: the four hierarchy counts of `dashboard.html`.
 *
 * A `null` count means the caller's scope cannot reach that level, and it
 * renders as "Not in your scope" -- never as `0`, which would claim the level
 * exists and is empty (BR-F16.1-2).
 */

import type { ReactNode } from 'react';
import { Briefcase, FileSignature, Layers, Network } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { DashboardScoped } from '@/api/client';

import {
  NOT_IN_SCOPE,
  currencyFoot,
  elementsFoot,
  formatCount,
  inactiveFoot,
  nothingImported,
} from './dashboardView';

interface CountCardProps {
  label: string;
  bar: number;
  icon: ReactNode;
  value: number | null;
  foot: ReactNode;
}

function CountCard({ label, bar, icon, value, foot }: CountCardProps) {
  const shown = formatCount(value);
  return (
    <div className={`card stat-card rv-bar-${bar}`}>
      <div className="stat-top">
        <span className="stat-label">{label}</span>
        <span className="stat-icon" aria-hidden="true">{icon}</span>
      </div>
      {shown === null ? (
        <div className="stat-value dash-out-of-scope">{NOT_IN_SCOPE}</div>
      ) : (
        <div className="stat-value num">{shown}</div>
      )}
      <div className="stat-foot">{shown === null ? 'Outside the hierarchy you own' : foot}</div>
    </div>
  );
}

export function StatCards({ body, canImport }: { body: DashboardScoped; canImport: boolean }) {
  const { counts, count_feet: feet } = body;
  const empty = nothingImported(counts);
  const importHint = empty ? (
    <>
      Nothing imported yet
      {canImport && (
        <>
          {' · '}
          <Link className="cell-link" to="/data-import">Import</Link>
        </>
      )}
    </>
  ) : null;

  return (
    <div className="grid grid-4">
      <CountCard
        label="Business Unit Groups"
        bar={1}
        icon={<Layers />}
        value={counts.business_unit_groups}
        foot={importHint ?? `${inactiveFoot(feet.groups_inactive)} · owned by DEM / CEM`}
      />
      <CountCard
        label="Business Units"
        bar={2}
        icon={<Network />}
        value={counts.business_units}
        foot={importHint ?? currencyFoot(feet.units_by_currency)}
      />
      <CountCard
        label="Clients"
        bar={3}
        icon={<Briefcase />}
        value={counts.clients}
        foot={importHint ?? inactiveFoot(feet.clients_inactive)}
      />
      <CountCard
        label="Engagements"
        bar={4}
        icon={<FileSignature />}
        value={counts.engagements}
        foot={importHint ?? elementsFoot(feet.elements_in_cycle, body.cycle !== null)}
      />
    </div>
  );
}
